import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedRecordLabel } from '@/i18n/catalogDisplayText';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import SheinSyncProgressBar from '@/shein-platform/components/SheinSyncProgressBar';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import { downloadCsv } from '@/shein-platform/modules/products/components/SheinPageToolbar';
import SheinOpsListToolbar from '@/shein-platform/components/SheinOpsListToolbar';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { openSheinPathInNewTab } from '@/shein-platform/constants/sheinRoutes';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import {
  batchPublishDrafts,
  SHEIN_BATCH_PUBLISH_CONCURRENCY,
} from '@/shein-platform/utils/sheinBatchPublishDrafts';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function formatBeijingDateTime(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n) => String(n).padStart(2, '0');
  // 后端常把北京墙钟标成 UTC(Z)，用 UTC 分量即北京时间；否则按 Asia/Shanghai
  const mislabeledAsUtc = /T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+]00:00)$/i.test(raw);
  const opts = {
    timeZone: mislabeledAsUtc ? 'UTC' : 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '00';
  return `${get('year')}/${get('month')}/${get('day')} ${pad(Number(get('hour')))}:${get('minute')}:${get('second')}`;
}

function DraftSkcThumb({ imageUrl }) {
  return <SheinProductThumb imageUrl={imageUrl} />;
}

/** 发品草稿箱：产品信息 / SKC 主图 */
export default function ProductDraftsPage() {
  const { t, i18n } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));

  const [filterDraft, setFilterDraft] = useState({ title: '', supplierCode: '' });
  const [query, setQuery] = useState({ keyword: '' });
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [toolbarBusy, setToolbarBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [batchPublishProgress, setBatchPublishProgress] = useState(null);
  const selectedRowsRef = useRef(new Map());

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedIds.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      if (row?.id != null && selectedIds.has(row.id)) map.set(row.id, row);
    });
  }, [items, selectedIds]);

  const loadDrafts = useCallback(async () => {
    if (!shopId) {
      setItems([]);
      setTotal(0);
      setSelectedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listPublishDrafts(shopId, {
        keyword: query.keyword || undefined,
        page,
        pageSize,
      });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      setTotal(Number(res?.total) || 0);
      // 跨页选中：翻页刷新列表时保留已选 id
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('draftBox.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, query.keyword, page, pageSize, queryNonce, t]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  useEffect(() => {
    setFilterDraft({ title: '', supplierCode: '' });
    setQuery({ keyword: '' });
    setPage(1);
    setQueryNonce((n) => n + 1);
    setSelectedIds(new Set());
  }, [shopId]);

  const selectableIds = useMemo(() => items.map((row) => row.id).filter(Boolean), [items]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds((prev) => togglePageSelection(prev, selectableIds, allSelected));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onQuery = () => {
    const parts = [filterDraft.title, filterDraft.supplierCode]
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (shopId) setLoading(true);
    setPage(1);
    setQuery({ keyword: parts.join(' ') });
    setQueryNonce((n) => n + 1);
  };

  const onReset = () => {
    setFilterDraft({ title: '', supplierCode: '' });
    setQuery({ keyword: '' });
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const onContinue = (item) => {
    if (item?.source === 'sync' && item?.platformSpuName) {
      const qs = new URLSearchParams();
      if (item.id != null) qs.set('draftId', String(item.id));
      const q = qs.toString();
      openSheinPathInNewTab(
        `products/edit/${encodeURIComponent(item.platformSpuName)}${q ? `?${q}` : ''}`,
      );
      return;
    }
    openSheinPathInNewTab(`products/publish?draftId=${encodeURIComponent(item.id)}`);
  };

  const onDelete = async (id) => {
    if (!shopId) return;
    const ok = await showSheinConfirm({
      title: t('draftBox.confirmDelete'),
      message: t('draftBox.deleteOne', { platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('draftBox.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    try {
      await sheinPlatformAPI.deletePublishDraft(shopId, id);
      showToast(t('draftBox.deleted'), 'success');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      loadDrafts();
    } catch (e) {
      showToast(e?.message || t('draftBox.deleteFailed'), 'error');
    }
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('draftBox.pickToDelete'), 'warning');
      return;
    }
    const ids = Array.from(selectedIds)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('draftBox.invalidSelection'), 'warning');
      return;
    }
    const confirmed = await showSheinConfirm({
      title: t('draftBox.batchDelete'),
      message: t('draftBox.batchDeleteMsg', { count: ids.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('draftBox.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;
    setToolbarBusy(true);
    try {
      const res = await sheinPlatformAPI.batchDeletePublishDrafts(shopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('draftBox.deletedCount', { count }), 'success');
      } else {
        showToast(t('draftBox.noneDeleted'), 'warning');
      }
      setSelectedIds(new Set());
      loadDrafts();
    } catch (e) {
      showToast(e?.message || t('draftBox.batchDeleteFailed'), 'error');
    } finally {
      setToolbarBusy(false);
    }
  };

  const writeDraftsCsv = (rows, filename) => {
    if (!rows.length) {
      showToast(t('draftBox.noneToExport'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['id', 'title', 'supplierCode', 'platformSpuName', 'source', 'createdAt', 'updatedAt'],
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        supplierCode: row.supplierCode,
        platformSpuName: row.platformSpuName,
        source: row.source,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
      })),
    );
    showToast(t('draftBox.exported', { count: rows.length }), 'success');
  };

  const exportFilteredDrafts = async () => {
    if (!shopId) {
      showToast(t('draftBox.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listPublishDrafts(shopId, {
          keyword: query.keyword || undefined,
          page: pageNo,
          pageSize: size,
        });
        return {
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total) || 0,
        };
      });
      writeDraftsCsv(all, `shein-drafts-filtered-${Date.now()}.csv`);
    } catch (e) {
      showToast(e?.message || t('draftBox.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPageDrafts = () => {
    writeDraftsCsv(items, `shein-drafts-page-${Date.now()}.csv`);
  };

  const exportSelectedDrafts = () => {
    const selected = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id))
      .filter(Boolean);
    if (!selected.length) {
      showToast(t('draftBox.pickToExport'), 'warning');
      return;
    }
    writeDraftsCsv(selected, `shein-drafts-selected-${Date.now()}.csv`);
  };

  const onBatchPublish = async () => {
    if (!shopId || selectedIds.size === 0) {
      showToast(t('draftBox.pickToPublish'), 'warning');
      return;
    }
    if (toolbarBusy) return;
    const selected = Array.from(selectedIds)
      .map((id) => selectedRowsRef.current.get(id) || { id })
      .filter((item) => item?.id);
    if (!selected.length) {
      showToast(t('draftBox.pickToPublish'), 'warning');
      return;
    }
    const confirmed = await showSheinConfirm({
      title: t('draftBox.batchPublish'),
      message: t('draftBox.batchPublishMsg', { count: selected.length, limit: SHEIN_BATCH_PUBLISH_CONCURRENCY }),
      tone: 'warning',
      confirmText: t('draftBox.startPublish'),
      cancelText: t('shop.cancel'),
    });
    if (!confirmed) return;

    setToolbarBusy(true);
    setBatchPublishProgress({
      visible: true,
      status: 'running',
      percent: 1,
      processed: 0,
      total: selected.length,
      message: t('draftBox.preparing'),
    });

    try {
      const result = await batchPublishDrafts({
        shopId,
        shopType: activeShop?.shopType,
        drafts: selected,
        concurrency: SHEIN_BATCH_PUBLISH_CONCURRENCY,
        onProgress: ({ processed, total, message }) => {
          const safeTotal = Math.max(1, Number(total) || selected.length);
          const done = Math.max(0, Number(processed) || 0);
          setBatchPublishProgress({
            visible: true,
            status: 'running',
            percent: Math.min(99, Math.round((done / safeTotal) * 100)),
            processed: done,
            total: safeTotal,
            message: message || t('draftBox.publishing'),
          });
        },
      });

      const okCount = result.success.length;
      const failCount = result.failed.length;
      setBatchPublishProgress({
        visible: true,
        status: failCount && !okCount ? 'failed' : 'success',
        percent: 100,
        processed: result.total,
        total: result.total,
        message: t('draftBox.done', { ok: okCount, fail: failCount }),
      });

      if (okCount) {
        const okIds = new Set(result.success.map((row) => row.draftId));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          okIds.forEach((id) => next.delete(id));
          return next;
        });
        loadDrafts();
      }

      if (okCount && !failCount) {
        showToast(t('draftBox.submitted', { count: okCount }), 'success');
      } else if (okCount && failCount) {
        const sample = result.failed.slice(0, 3)
          .map((row) => t('draftBox.failRow', { title: row.title || row.draftId, message: row.message || t('draftBox.failed') }))
          .join(t('draftBox.failSep'));
        showToast(t('draftBox.partial', { ok: okCount, fail: failCount, sample, more: failCount > 3 ? '…' : '' }), 'warning');
      } else {
        const sample = result.failed.slice(0, 3)
          .map((row) => t('draftBox.failRow', { title: row.title || row.draftId, message: row.message || t('draftBox.failed') }))
          .join(t('draftBox.failSep'));
        showToast(t('draftBox.allFailed', { sample, more: failCount > 3 ? '…' : '' }), 'error');
      }
    } catch (e) {
      setBatchPublishProgress({
        visible: true,
        status: 'failed',
        percent: 0,
        processed: 0,
        total: selected.length,
        message: e?.message || t('draftBox.batchPublishFailed'),
      });
      showToast(e?.message || t('draftBox.batchPublishFailed'), 'error');
    } finally {
      setToolbarBusy(false);
      window.setTimeout(() => setBatchPublishProgress(null), 1800);
    }
  };

  const colSpan = 6;

  return (
    <>
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedIds.size}
            busy={toolbarBusy || loading || exporting}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || toolbarBusy || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedIds.size,
              onExportFiltered: exportFilteredDrafts,
              onExportPage: exportPageDrafts,
              onExportSelected: exportSelectedDrafts,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || toolbarBusy}
            extraActions={[
              {
                key: 'publish',
                label: t('draftBox.batchPublish'),
                tone: 'primary',
                disabled: !shopId || selectedIds.size === 0 || toolbarBusy,
                onClick: onBatchPublish,
              },
            ]}
          />
        )}
      >
        {batchPublishProgress?.visible ? (
          <div style={{ marginBottom: 12 }}>
            <SheinSyncProgressBar progress={batchPublishProgress} />
          </div>
        ) : null}
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('draftBox.title')}</span>
              <input
                className="spo-input"
                value={filterDraft.title}
                placeholder={t('draftBox.titlePlaceholder')}
                disabled={!shopId}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('draftBox.supplier')}</span>
              <input
                className="spo-input"
                value={filterDraft.supplierCode}
                placeholder={t('draftBox.supplierPlaceholder')}
                disabled={!shopId}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
          </div>

          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset} disabled={!shopId}>{t('draftBox.reset')}</button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!shopId}>{t('draftBox.query')}</button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard tabs={shopDimensionTabs} loading={Boolean(shopId) && loading} loadingLabel={t('draftBox.querying')}>
        <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
          <div className="wf-tasks-table-panel">
            <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
              <table className="wf-tasks-table platform-product-table platform-product-table--shein platform-product-table--drafts">
                <colgroup>
                  <col className="shein-product-col--check" />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="shein-product-cell--spu-check"
                      onClick={() => {
                        if (!shopId || loading || selectableIds.length === 0) return;
                        toggleAll();
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={t('draftBox.selectAll')}
                        disabled={!shopId || loading || selectableIds.length === 0}
                        checked={allSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">{t('draftBox.productInfo')}</th>
                    <th scope="col">SKC</th>
                    <th scope="col">{t('draftBox.type')}</th>
                    <th scope="col" className="shein-draft-th--time">{t('draftBox.createdAt')}</th>
                    <th scope="col" className="shein-draft-th--actions">{t('draftBox.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!shopId || loading || items.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan} className="wf-tasks-table__empty">
                        <div className="funding-empty-state">
                          <p className="funding-empty-state__title">
                            {!shopId ? (allShopsScope ? t('draftBox.pickConcrete') : t('draftBox.pickShop')) : loading ? t('draftBox.loading') : t('draftBox.noDrafts')}
                          </p>
                          {!shopId || loading ? null : (
                            <p className="funding-empty-state__description">
                              {t('draftBox.emptyHintShein')}
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.flatMap((item) => {
                      const skcItems = Array.isArray(item.skcItems) && item.skcItems.length
                        ? item.skcItems
                        : [{ colorLabel: null, skcName: null, imageUrl: null, merchantSkus: [] }];
                      const typeLabel = item.typeLabel
                        || (item.source === 'sync' ? t('draftBox.sourceSync') : t('draftBox.sourceNew'));
                      const rowSpan = Math.max(1, skcItems.length);

                      return skcItems.map((skc, skcIdx) => {
                        const isFirst = skcIdx === 0;

                        return (
                          <tr
                            key={`${item.id}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
                            className={`shein-product-row shein-draft-row${isFirst ? '' : ' shein-draft-row--skc-child'}`}
                          >
                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-product-cell--spu-check"
                                  onClick={() => toggleOne(item.id)}
                                >
                                  <input
                                    type="checkbox"
                                    aria-label={t('draftBox.selectDraft', { title: item.title || item.id })}
                                    checked={selectedIds.has(item.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleOne(item.id)}
                                  />
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-product-cell--info"
                                >
                                  <div className="shein-product-info shein-product-info--text-only shein-draft-product-info">
                                    <div className="shein-product-info__text">
                                      <HoverCopyField
                                        value={item.title || t('draftBox.unnamed')}
                                        className="shein-product-info__title"
                                        popoverCopy
                                      />
                                      <HoverCopyField
                                        label="SPU"
                                        value={item.platformSpuName || ''}
                                        className="shein-product-info__meta"
                                        separator=": "
                                      />
                                      <HoverCopyField
                                        label={t('draftBox.supplier')}
                                        value={item.supplierCode || ''}
                                        className="shein-product-info__meta"
                                        separator=": "
                                      />
                                      <div className="shein-product-info__meta">
                                        {t('draftBox.category', { label: item.categoryLabel || '—' })}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </>
                            ) : null}

                            <td className="shein-product-cell shein-product-cell--spec">
                              <div className="shein-product-spec shein-draft-skc">
                                <DraftSkcThumb imageUrl={skc.imageUrl} />
                                <div className="shein-product-spec__text">
                                  <div className="shein-product-spec__color">
                                    {skc.colorLabel || '—'}
                                  </div>
                                  <HoverCopyField
                                    label="SKC"
                                    value={skc.skcName || ''}
                                    className="shein-product-spec__skc"
                                    separator=": "
                                  />
                                </div>
                              </div>
                            </td>

                            {isFirst ? (
                              <>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-draft-cell--type"
                                >
                                  {localizedRecordLabel(t, typeLabel, i18n.language)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell platform-product-table__muted shein-draft-cell--time"
                                >
                                  {formatBeijingDateTime(item.createdAt || item.updatedAt)}
                                </td>
                                <td
                                  rowSpan={rowSpan}
                                  className="shein-product-cell shein-draft-cell--actions"
                                >
                                  <div className="shein-draft-actions">
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      onClick={() => onContinue(item)}
                                    >{t('draftBox.edit')}</button>
                                    <button
                                      type="button"
                                      className="wf-tasks-actions__link"
                                      onClick={() => onDelete(item.id)}
                                    >{t('draftBox.delete')}</button>
                                  </div>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      });
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <SheinOpsPager
            page={page}
            pageSize={pageSize}
            total={total}
            loading={loading}
            disabled={!shopId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel={t('draftBox.pager')}
          />
        </div>
      </ProductListCard>
    </>
  );
}
